import { useContext } from 'react'

// Constants
import { useIsZillow } from '../utility'

// Context
import { AppContext, ZillowContext } from '../../contexts'

// Hooks
import { useBuyerSettingsWithDefaults } from '../buyerSettings'
import { useZillowListingUrl } from '../browser'
import { useIsOwner } from '../branding'

// Queries
import {
  useMarketTempIndicatorQuery,
  useListingEvaluationQuery,
  useAppreciationIndicatorQuery,
  useListingRentalPotentialQuery,
} from './queries'
import {
  getListingRecommendationStatus,
  getListingRecommendationDecorators,
  getListingZipcode,
  getListingStreet,
  getCashPositionIndicator,
  getMarketTempIndicator,
  getAppreciationIndicator,
  getHomeownerListingRecommendationStatus,
} from './getters'

// Regex
import { listingRegex } from '../../constants/regex'

const validate = (address, listingPrice) => {
  const errorMessages = []
  if (address && typeof address !== 'string') {
    errorMessages.push("Address provided is not of type 'string'")
  }
  if (listingPrice && typeof listingPrice !== 'number') {
    errorMessages.push("Listing Price provided is not of type 'number'")
  }
  if (typeof listingPrice === 'number' && listingPrice <= 0) {
    errorMessages.push('Listing Price provided is not a positive value')
  }

  const addressValid = address && address.length
  const priceValid = typeof listingPrice === 'number'

  return {
    refetch: () => {
      // refetch from react query
    },
    isError: errorMessages.length,
    error: errorMessages.length ? new Error(errorMessages.join(', ')) : null,
    isFetched: errorMessages.length === 0 && addressValid && priceValid,
  }
}
const useZillowListingData = () => {
  const url = useZillowListingUrl()
  const { address, listingPrice, isAddressError, isListingPriceError } =
    useContext(ZillowContext)

  if (listingRegex.test(url)) {
    const match = url.match(listingRegex)
    const id = match?.groups?.id

    const validation = validate(address, listingPrice)
    const zillowDomError = isAddressError || isListingPriceError

    return {
      data: {
        id,
        url,
        address,
        listingPrice,
      },
      ...validation,
      zillowDomError,
      isError: validation.isError || isAddressError || isListingPriceError,
      isFetched: validation.isFetched || isAddressError || isListingPriceError,
    }
  }
}

const useProvidedListingData = () => {
  const { address, listingPrice } = useContext(AppContext)

  const validation = validate(address, listingPrice)

  return {
    data: {
      address,
      listingPrice,
    },
    ...validation,
  }
}

const useRawListingData = () => {
  const isZillow = useIsZillow()
  const zillowFields = useZillowListingData()
  const providedFields = useProvidedListingData()

  if (isZillow) {
    return zillowFields
  }

  return providedFields
}

// Need anything returned by these {5} endpoints in one tidy package?
// Use with CAUTION: {5} endpoints means increased edge cases, only use optimisticly
export const useFullListingData = () => {
  const { marketTempSliderPrice, appreciationSliderPercent, includeRental } =
    useContext(AppContext)

  const rawListing = useRawListingData()

  // Dependant on rawListing
  const { address, listingPrice } = rawListing?.data ?? {}
  const listingEvaluation = useListingEvaluationQuery(
    address,
    listingPrice,
    marketTempSliderPrice,
    appreciationSliderPercent,
    includeRental
  )
  const marketTempIndicator = useMarketTempIndicator(address, listingPrice)
  const appreciationIndicator = useAppreciationIndicatorQuery(
    address,
    listingPrice
  )
  const listingRentalPotential = useListingRentalPotentialQuery(address)

  return {
    ...rawListing.data,
    ...listingEvaluation?.data,
    ...appreciationIndicator?.data,
    ...marketTempIndicator?.data,
    ...listingRentalPotential.data,
  }
}

// For most UI, determines "status" (aka color) and drives a lot of copy
// Very number crunchy with async dependancies, ensure safe "default" behavior
export const useListingDisplay = () => {
  const { marketTempSliderPrice, appreciationSliderPercent, includeRental } =
    useContext(AppContext)

  // Data
  const buyerSettings = useBuyerSettingsWithDefaults()
  const rawListing = useRawListingData()

  // Dependant on rawListing
  const { address, listingPrice } = rawListing?.data ?? {}
  const listingEvaluation = useListingEvaluationQuery(
    address,
    listingPrice,
    marketTempSliderPrice,
    appreciationSliderPercent,
    includeRental
  )

  const {
    maxBudget,
    yearsInHome,
    currentAddress,
    estimatedDownPaymentFromEquity,
    usingDefaultDownPayment,
    hasUpdatedListingsBotProfile,
  } = buyerSettings

  const {
    savingsValues,
    breakEvenYears,
    monthlyCost,
    monthlyCostBreakdown,
    totalBuyCost,
    totalCurrentCost,
    mostRecentInterestRate,
  } = listingEvaluation?.data ?? {}

  const savingsValue =
    savingsValues && savingsValues.length >= yearsInHome
      ? savingsValues[yearsInHome - 1]
      : null

  const streetAddress = getListingStreet(address)

  let status

  const isOwner = useIsOwner()

  if (isOwner) {
    status = getHomeownerListingRecommendationStatus(
      totalBuyCost,
      monthlyCost,
      maxBudget
    )
  } else {
    status = getListingRecommendationStatus(
      savingsValue,
      monthlyCost,
      maxBudget
    )
  }

  const budgetDiff = monthlyCost - maxBudget

  const decoratorStatus = listingEvaluation.isPreviousData ? null : status
  const decorators = getListingRecommendationDecorators(decoratorStatus)

  return {
    ...listingEvaluation,
    address,
    listingPrice,
    yearsInHome,
    currentAddress,
    estimatedDownPaymentFromEquity,
    usingDefaultDownPayment,
    hasUpdatedListingsBotProfile,
    monthlyCost,
    streetAddress,
    savingsValue,
    savingsValues,
    breakEvenYears,
    monthlyCostBreakdown,
    status,
    totalBuyCost,
    totalCurrentCost,
    mostRecentInterestRate,
    budgetDiff,
    zillowDomError: rawListing?.zillowDomError,
    ...decorators, // color, text, theme
    refetch: () => {
      buyerSettings?.refetch()
      rawListing?.refetch()
      listingEvaluation?.refetch()
    },
    isError:
      buyerSettings?.isError ||
      rawListing?.isError ||
      listingEvaluation?.isError,
    error:
      buyerSettings?.error || rawListing?.error || listingEvaluation?.error,
    isFetched:
      rawListing?.isError ||
      (buyerSettings?.isFetched &&
        rawListing?.isFetched &&
        listingEvaluation?.isFetched &&
        !listingEvaluation.isPreviousData),
  }
}

export const useAppreciationIndicator = () => {
  const rawListing = useRawListingData()
  const indicator = useAppreciationIndicatorQuery(
    rawListing?.data?.address,
    rawListing?.data?.listingPrice
  )

  const { annualAppreciationPercent, yearsOfData } = indicator?.data ?? {}

  return getAppreciationIndicator(annualAppreciationPercent, yearsOfData)
}

export const useMarketTempIndicator = () => {
  const rawListing = useRawListingData()
  const indicator = useMarketTempIndicatorQuery(
    rawListing?.data?.address,
    rawListing?.data?.listingPrice
  )

  const { marketTemp } = indicator?.data ?? {}

  return getMarketTempIndicator(marketTemp)
}

export const useCashPositionIndicator = () => {
  const { marketTempSliderPrice, appreciationSliderPercent, includeRental } =
    useContext(AppContext)

  const buyerSettings = useBuyerSettingsWithDefaults()
  const rawListing = useRawListingData()
  const evaluation = useListingEvaluationQuery(
    rawListing?.data?.address,
    rawListing?.data?.listingPrice,
    marketTempSliderPrice,
    appreciationSliderPercent,
    includeRental
  )
  const { monthlyCost } = evaluation?.data ?? {}

  return getCashPositionIndicator(monthlyCost, buyerSettings?.maxBudget)
}

export const useListingError = () => {
  const { isError, error, zillowDomError } = useListingDisplay()

  return {
    isError,
    error,
    isZillowDomError: zillowDomError,
  }
}

export { getListingZipcode, useRawListingData }
